// @flow

import type { AttributeType, IFilter, ModelOptions } from "../types";
import FilterModel from "./FilterModel";

/**
 * StringFilterModel can handle string filters with multiple setting
 */
export default class StringFilterModel extends FilterModel implements IFilter {
  _isValid: boolean = true;
  _value: string | null;
  _inputvalue: string;

  /**
   */
  constructor(
    data: Object,
    contributions: Object,
    modelOptions?: ModelOptions,
  ) {
    super(data, contributions, modelOptions);

    this.update(this.attribute, data.value ?? "");

    // when formatted value set this to initial input value
    this._inputvalue = this._value ?? "";
  }

  /**
   * Retrieve the parameters with its value for this filter
   */
  get params(): Array<{ name: string, value: ?string }> {
    if (!this.param) {
      return [];
    }

    return [
      {
        name: this.param,
        value: this.removeFormat(this.value),
      },
    ];
  }

  /**
   * Getting the value of the filter
   */
  get value(): string | null {
    return this._value;
  }

  /**
   */
  get inputvalue(): string {
    return this._inputvalue;
  }

  /**
   * Reset the value of this filter to undefined
   */
  reset(): StringFilterModel {
    this._inputvalue = "";
    this._value = null;

    return this;
  }

  /**
   */
  get isValid(): boolean {
    return this._isValid;
  }

  /**
   * Update this filter with input name and value
   */
  update(attribute: AttributeType, value: string) {
    this._isValid = true;
    this._inputvalue = typeof value === "string" ? value : value.toString();

    if (this.shouldHandleFormat()) {
      const values = this.isMultiple
        ? this._inputvalue.split(",").map((val) => val.trim())
        : [this._inputvalue.trim()];

      const outputValues = [];
      for (const val of values) {
        const formattedValue = this.formatValue(val);
        if (formattedValue !== "") {
          outputValues.push(formattedValue);

          if (!this.attribute.validate(formattedValue)) {
            this._isValid = false;
          }
        }
      }

      this._value = outputValues.join(",");
    } else {
      this._value = value;
    }
  }

  /**
   */
  get operator(): string {
    return this.getContribution("operator", "");
  }

  /**
   */
  formatValue(value: string): string {
    if (this.shouldHandleFormat()) {
      return this.attribute.formatValue(value);
    }
    return value;
  }

  /**
   */
  isBSN(): boolean {
    return this.layouthint.has("bsn");
  }

  /**
   */
  isIBAN(): boolean {
    return this.layouthint.has("iban");
  }

  /**
   */
  isZipcode(): boolean {
    return this.layouthint.has("zipcode");
  }

  /**
   */
  removeFormat(value: ?string): string {
    if (value == null || value.toString() === "") {
      return "";
    }

    if (this.shouldHandleFormat()) {
      // $FlowIssue
      return value.replaceAll(/[^a-z0-9,]/gi, "");
    }

    return value;
  }

  /**
   */
  shouldHandleFormat(): boolean {
    return (
      (this.operator === "exactly" || this.operator === "isNot") &&
      (this.isIBAN() || this.isZipcode() || this.isBSN())
    );
  }

  /**
   */
  get formdata(): { [string]: any } | null {
    return {
      [this.param]: this.value,
    };
  }
}
